import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Send, Lock } from "lucide-react";
import { SERVICES_DROPDOWN } from "../../data/servicesData";
import emailjs from "@emailjs/browser";

// EmailJS Configuration from environment variables
const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID || "service_7ulzw5a";
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || "template_qzjbmgv";
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || "6AwoN92OuqrpvhI7H";

export function LeadForm({ accent = "#dc2626", preselectedService = "" }) {
  const [form, setForm] = useState({
    name: "",
    company_name: "",
    phone: "",
    email: "",
    service_interested: preselectedService,
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.company_name || !form.service_interested) {
      toast.error("Please fill all required fields.");
      return;
    }
    setLoading(true);
    try {
      // Send email via EmailJS
      const templateParams = {
        from_name: form.name,
        from_email: form.email,
        phone: form.phone,
        company_name: form.company_name,
        service_interested: form.service_interested,
        message: form.message || "No additional message",
      };

      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        EMAILJS_PUBLIC_KEY
      );
      
      setSubmitted(true);
      toast.success("Request submitted! We'll reach out within 24 hours.");
      setForm({ name: "", company_name: "", phone: "", email: "", service_interested: "", message: "" });
    } catch (error) {
      console.error("EmailJS Error:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center py-16 px-8 bg-white rounded-3xl border border-green-200 shadow-xl"
      >
        <div
          className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center"
          style={{ background: `${accent}15` }}
        >
          <Send size={32} style={{ color: accent }} />
        </div>
        <h3 className="font-heading text-2xl font-black text-slate-900 mb-3">
          Thank You!
        </h3>
        <p className="text-slate-500 mb-8 font-body">
          Our team will contact you shortly.
        </p>
        <button
          data-testid="submit-another-btn"
          onClick={() => setSubmitted(false)}
          className="font-extrabold text-sm px-8 py-3 rounded-full text-white transition-all active:scale-95 font-body"
          style={{ background: accent }}
        >
          Submit Another Request
        </button>
      </motion.div>
    );
  }

  return (
    <motion.form
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      onSubmit={handleSubmit}
      data-testid="lead-form"
      className="bg-white rounded-3xl border border-slate-200 p-8 md:p-10 shadow-xl"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {[
          { label: "Full Name", key: "name", placeholder: "John Doe", type: "text" },
          { label: "Email Address", key: "email", placeholder: "john@company.com", type: "email" },
          { label: "Phone Number", key: "phone", placeholder: "+91 98765 43210", type: "tel" },
          { label: "Company Name", key: "company_name", placeholder: "Your Company", type: "text" },
        ].map(({ label, key, placeholder, type }) => (
          <div key={key}>
            <label className="block text-[13px] font-bold text-slate-700 mb-2 font-body">
              {label} <span className="text-red-500">*</span>
            </label>
            <input
              data-testid={`lead-form-${key}`}
              type={type}
              placeholder={placeholder}
              value={form[key]}
              onChange={(e) => handleChange(key, e.target.value)}
              className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm bg-slate-50 outline-none font-body transition-colors"
              style={{ "--tw-ring-color": accent }}
              onFocus={(e) => (e.target.style.borderColor = accent)}
              onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
            />
          </div>
        ))}
      </div>

      <div className="mt-5">
        <label className="block text-[13px] font-bold text-slate-700 mb-2 font-body">
          Service Interested In <span className="text-red-500">*</span>
        </label>
        <select
          data-testid="lead-form-service"
          value={form.service_interested}
          onChange={(e) => handleChange("service_interested", e.target.value)}
          className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm bg-slate-50 outline-none font-body appearance-none"
          onFocus={(e) => (e.target.style.borderColor = accent)}
          onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
        >
          <option value="">Select a service...</option>
          {SERVICES_DROPDOWN.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      <div className="mt-5">
        <label className="block text-[13px] font-bold text-slate-700 mb-2 font-body">
          Message
        </label>
        <textarea
          data-testid="lead-form-message"
          rows={4}
          placeholder="Tell us about your requirements..."
          value={form.message}
          onChange={(e) => handleChange("message", e.target.value)}
          className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm bg-slate-50 outline-none font-body resize-none"
          onFocus={(e) => (e.target.style.borderColor = accent)}
          onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
        />
      </div>

      <button
        data-testid="lead-form-submit-btn"
        type="submit"
        disabled={loading}
        className="mt-6 w-full text-white font-extrabold text-[15px] py-4 rounded-2xl transition-all active:scale-[0.98] font-body disabled:opacity-50 flex items-center justify-center gap-2"
        style={{
          background: `linear-gradient(135deg, ${accent}, ${accent}cc)`,
          boxShadow: `0 12px 40px ${accent}35`,
        }}
      >
        {loading ? "Submitting..." : "Submit Request"}
        {!loading && <Send size={16} />}
      </button>

      <p className="text-center text-slate-400 text-xs mt-4 flex items-center justify-center gap-1.5 font-body">
        <Lock size={12} /> Your information is secure. We never share your data.
      </p>
    </motion.form>
  );
}
